import React from "react";
import { useQuery } from "@tanstack/react-query";
import Navbar from "../components/Navbar";
import { fetchMentoresWithDetails } from "../services/mentorService";
import { supabase } from "@/integrations/supabase/client";
import { fetchAllActiveMentorshipOfferings } from "@/services/mentorshipService";
import { getAllActiveDigitalContents } from "@/services/digitalContent/publicDigitalContentService";
import { useHeroBackground } from "@/hooks/useHeroBackground";

// Import refactored components
import { HeroSection } from "@/components/home/HeroSection";
import { HowItWorksSection } from "@/components/home/HowItWorksSection";
import { MentorsSection } from "@/components/home/MentorsSection";
import { GamesSection } from "@/components/home/GamesSection";
import { MentorshipsSection } from "@/components/home/MentorshipsSection";
import { TestimonialsSection } from "@/components/home/TestimonialsSection";
import { CTASection } from "@/components/home/CTASection";
import { GuidesSection } from "@/components/home/GuidesSection";
import { CommunitiesSection } from "@/components/home/CommunitiesSection";
import { HeroBackgroundGradient } from "@/components/layout/HeroBackgroundGradient";
import { useFeaturedItemsForHome } from "@/hooks/useFeaturedItems";

const Index = () => {
  const { settings, loading } = useHeroBackground();
  
  // Buscar itens em destaque para a home
  const { data: featuredMentors } = useFeaturedItemsForHome('mentor');
  const { data: featuredMentorias } = useFeaturedItemsForHome('mentoria');
  const { data: featuredComunidades } = useFeaturedItemsForHome('comunidade');
  
  React.useEffect(() => {
    console.log('🏠 [Index] Hero background settings:', settings);
    console.log('🏠 [Index] Loading:', loading);
    console.log('🏠 [Index] Featured mentors:', featuredMentors);
    console.log('🏠 [Index] Featured mentorias:', featuredMentorias);
    console.log('🏠 [Index] Featured comunidades:', featuredComunidades);
  }, [settings, loading, featuredMentors, featuredMentorias, featuredComunidades]);
  
  // Fetch top mentors (com sistema de destaques)
  const {
    data: mentors,
    isLoading: mentorsLoading,
    error: mentorsError
  } = useQuery({
    queryKey: ["featured-mentors", featuredMentors],
    queryFn: async () => {
      const allMentors = await fetchMentoresWithDetails();
      
      if (featuredMentors && featuredMentors.length > 0) {
        // Usar mentores em destaque, ordenados por posição
        const featuredMentorIds = featuredMentors.map(item => item.item_id);
        const featuredMentorsList = allMentors.filter(mentor => featuredMentorIds.includes(mentor.profile_id));
        
        // Ordenar por posição
        const sortedFeaturedMentors = featuredMentorsList.sort((a, b) => {
          const positionA = featuredMentors.find(item => item.item_id === a.profile_id)?.position || 999;
          const positionB = featuredMentors.find(item => item.item_id === b.profile_id)?.position || 999;
          return positionA - positionB;
        });
        
        return sortedFeaturedMentors;
      }
      
      // Fallback: primeiros 3 mentores
      return allMentors.slice(0, 3);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 3,
    enabled: true // Ensure query is always enabled
  });

  // Fetch popular games
  const {
    data: games
  } = useQuery({
    queryKey: ["popular-games"],
    queryFn: async () => {
      const { data } = await supabase.from("games").select("id, name, cover_url");
      if (!data) return [];
      
      // Buscar número de mentores para cada jogo e ordenar
      const gamesWithMentorCount = await Promise.all(
        data.map(async (game) => {
          const { data: mentorData, error } = await supabase
            .from("mentorship_offerings")
            .select("mentor_id")
            .eq("game_id", game.id)
            .eq("is_active", true);
          
          if (error) {
            console.error("Error fetching mentor count for game:", game.name, error);
            return { ...game, mentorCount: 0 };
          }
          
          const uniqueMentors = new Set(mentorData?.map(offering => offering.mentor_id) || []);
          return { ...game, mentorCount: uniqueMentors.size };
        })
      );
      
      // Ordenar jogos: 1º por número de mentores (decrescente), 2º por nome (alfabética)
      const sortedGames = gamesWithMentorCount.sort((a, b) => {
        if (b.mentorCount !== a.mentorCount) {
          return b.mentorCount - a.mentorCount;
        }
        return a.name.localeCompare(b.name, 'pt-BR');
      });
      
      // Retornar apenas os primeiros 4 jogos
      return sortedGames.slice(0, 4);
    },
    staleTime: 60000
  });

  // Fetch featured mentorships (com sistema de destaques)
  const {
    data: mentorships
  } = useQuery({
    queryKey: ["featured-mentorships", featuredMentorias],
    queryFn: async () => {
      const offerings = await fetchAllActiveMentorshipOfferings();
      
      if (featuredMentorias && featuredMentorias.length > 0) {
        // Usar mentorias em destaque, ordenadas por posição
        const featuredMentoriaIds = featuredMentorias.map(item => item.item_id);
        const featuredMentoriasList = offerings.filter(mentoria => featuredMentoriaIds.includes(mentoria.id));
        
        // Ordenar por posição
        const sortedFeaturedMentorias = featuredMentoriasList.sort((a, b) => {
          const positionA = featuredMentorias.find(item => item.item_id === a.id)?.position || 999;
          const positionB = featuredMentorias.find(item => item.item_id === b.id)?.position || 999;
          return positionA - positionB;
        });
        
        return sortedFeaturedMentorias;
      }
      
      // Fallback: primeiras 3 mentorias
      return offerings.slice(0, 3);
    },
    staleTime: 60000
  });

  // Fetch featured communities (com sistema de destaques)
  const {
    data: communities,
    isLoading: communitiesLoading,
    error: communitiesError
  } = useQuery({
    queryKey: ["featured-communities", featuredComunidades],
    queryFn: async () => {
      const { fetchCommunities } = await import("@/services/communityService");
      const result = await fetchCommunities({ limit: 50, is_active: true }); // Buscar mais para filtrar
      
      if (featuredComunidades && featuredComunidades.length > 0) {
        // Usar comunidades em destaque, ordenadas por posição
        const featuredComunidadeIds = featuredComunidades.map(item => item.item_id);
        const featuredComunidadesList = result.communities.filter(comunidade => featuredComunidadeIds.includes(comunidade.id));
        
        // Ordenar por posição
        const sortedFeaturedComunidades = featuredComunidadesList.sort((a, b) => {
          const positionA = featuredComunidades.find(item => item.item_id === a.id)?.position || 999;
          const positionB = featuredComunidades.find(item => item.item_id === b.id)?.position || 999;
          return positionA - positionB;
        });
        
        return sortedFeaturedComunidades;
      }
      
      // Fallback: primeiras 3 comunidades
      return result.communities.slice(0, 3);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 3,
    enabled: true
  });

  // Fetch guides (conteúdos digitais ativos)
  const { data: guides } = useQuery({
    queryKey: ["home-guides"],
    queryFn: getAllActiveDigitalContents,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });



  // Smooth scroll effect for section navigation
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  const hasHeroBackground = settings?.background_image_url && !loading;

  return (
    <div className={`min-h-screen overflow-x-hidden ${!hasHeroBackground ? 'bg-background' : ''}`}>
      <Navbar />
      
      {/* Hero Section - usa o HeroBackgroundWrapper para background dinâmico */}
      <HeroSection scrollToSection={scrollToSection} />
      
      {/* Content with background for sections after hero */}
      <div className="bg-background">
        {/* How It Works Section - COM DEGRADÊ */}
        <HeroBackgroundGradient>
          <HowItWorksSection />
        </HeroBackgroundGradient>
        
        {/* Mentors Section */}
        <MentorsSection 
          mentors={mentors} 
          isLoading={mentorsLoading} 
          error={mentorsError}
          featuredItems={featuredMentors}
        />
        
        {/* Games Section */}
        <GamesSection games={games} />
        
        {/* Mentorships Section */}
        <MentorshipsSection 
          mentorships={mentorships}
          featuredItems={featuredMentorias}
        />
        
        {/* Guides Section */}
        <GuidesSection guides={guides} />
        
        {/* Communities Section */}
        <CommunitiesSection 
          communities={communities} 
          isLoading={communitiesLoading} 
          error={communitiesError}
          featuredItems={featuredComunidades}
        />
        
        {/* Testimonials Section */}
        <TestimonialsSection />
        
        {/* CTA Section */}
        <CTASection />
      </div>
    </div>
  );
};

export default Index;
